import React from 'react'
import Tooltip from '../ToolTip'
import {
  FaIcon,
  faMoneyBillTrendUp,
  faPrint,
  faListCheck,
  faCalculator,
  faArrowsRotate,
  faTrash,
} from '@/lib/fa'
import { generatePath, Link, useLocation } from 'react-router-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { driversRoutesMap } from '../../routes-config'
import { TimesheetViewMode } from '../../tabs/registry/components'

import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { YearMonthsValue } from '../../types/date'
import MonthMultiPickerButton from '../datepickers/MonthMultiPickerButton'
import { useSideSelectionStore } from '../../stores/sideSelectionStore'
import DividerVertical from '../DividerVertical'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'

interface TimeSheetActionBarProps {
  companyId: string
  onPrint?: () => void
  onDelete?: () => void
  onRefresh?: () => void
  onCalculate?: () => void
  /** Zakres tego okresu jest juz liczony - blokujemy i mowimy przez kogo. */
  calculateBusy?: boolean
  calculateBusyText?: string
  period: YearMonthsValue
  setPeriod: (next: YearMonthsValue) => void
  printShortcuts?: React.ReactNode
}

const TimeSheetActionBar = ({
  companyId,
  onPrint,
  onDelete,
  onRefresh,
  onCalculate,
  calculateBusy = false,
  calculateBusyText,
  period,
  setPeriod,
  printShortcuts,
}: TimeSheetActionBarProps) => {
  const { pathname } = useLocation()
  const viewMode: TimesheetViewMode = pathname.includes('bonusAndRewards')
    ? 'bonus-reward'
    : 'group-view'

  const { sideCheckedIds } = useSideSelectionStore()
  const { basicDrivers } = useDrivers()
  const { t } = useLocalize()
  // Blokada wydrukow jest wspolna dla calej aplikacji - patrz printJobStore.
  const { busyTooltip } = usePrintJobGuard()

  const hasSelection = sideCheckedIds.length > 0

  const allowCalculate = React.useMemo(() => {
    return (
      sideCheckedIds.length > 0 && !!period.year && period.months.length > 0
    )
  }, [period, sideCheckedIds])

  const selectedMonths = React.useMemo(() => {
    if (!period || !period.year || !period.months || period.months.length === 0)
      return []
    return period.months.map(
      (month) => `${period.year}-${String(month).padStart(2, '0')}`,
    )
  }, [period])

  const handleMonthsChange = React.useCallback(
    (months: string[]) => {
      if (months.length === 0) return
      const parsedMonths = months.map((m) => {
        const [year, month] = m.split('-').map(Number)
        return { year, month }
      })
      const year = parsedMonths[0].year
      const monthNumbers = parsedMonths.map((m) => m.month)
      setPeriod({ year, months: monthNumbers })
    },
    [setPeriod],
  )

  return (
    <div className="tw-z-[100] tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
      {/* Left Side */}
      <div className="tw-flex tw-items-center tw-gap-4">
        {/* View toggle */}
        <div className="tw-flex tw-items-center tw-gap-4">
          <Tooltip text={t('timesheet.list')} checked>
            <Link
              to={generatePath(driversRoutesMap.driverGroupTimesheet, {
                companyId,
              })}
            >
              <FaIcon
                icon={faListCheck}
                className={`tw-h-4 tw-w-4 ${
                  viewMode !== 'bonus-reward'
                    ? 'tw-text-[#2448bb]'
                    : 'tw-text-[#6b77a0]'
                }`}
              />
              {viewMode === 'group-view' && (
                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-h-[3px] tw-w-[3px] -tw-translate-x-1/2 tw-translate-y-[6px] tw-rounded-[4px] tw-bg-[#2448bb]" />
              )}
            </Link>
          </Tooltip>
          <Tooltip text={t('timesheet.bonus-and-reward')} align="start">
            <Link
              to={generatePath(
                driversRoutesMap.driverGroupTimesheetBonusAndRewards,
                { companyId },
              )}
            >
              <FaIcon
                icon={faMoneyBillTrendUp}
                className={`tw-h-4 tw-w-4 ${
                  viewMode === 'bonus-reward'
                    ? 'tw-text-[#2448bb]'
                    : 'tw-text-[#6b77a0]'
                }`}
              />
              {viewMode === 'bonus-reward' && (
                <div className="tw-absolute tw-bottom-0 tw-left-1/2 tw-h-[3px] tw-w-[3px] -tw-translate-x-1/2 tw-translate-y-[6px] tw-rounded-[4px] tw-bg-[#2448bb]" />
              )}
            </Link>
          </Tooltip>
        </div>

        {hasSelection && (
          <>
            <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]" />

            <MonthMultiPickerButton
              initialSelectedMonths={selectedMonths}
              onChange={handleMonthsChange}
              useRomanNumerals={false}
              companyId={companyId}
            />
            <DividerVertical />
            <Tooltip
              text={calculateBusy ? (calculateBusyText ?? 'Przelicz') : 'Przelicz'}
              onClick={onCalculate}
              disabled={!allowCalculate || calculateBusy}
            >
              <div className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faCalculator}
                  className={`tw-h-4 tw-w-4 ${allowCalculate && !calculateBusy ? 'tw-text-primary' : 'tw-text-gray-400'}`}
                />
                <span
                  className={`tw-text-sm tw-font-medium ${allowCalculate && !calculateBusy ? 'tw-text-primary' : 'tw-text-gray-400'}`}
                >
                  Przelicz
                </span>
              </div>
            </Tooltip>
          </>
        )}
      </div>

      <div className="tw-flex tw-items-center tw-gap-2">
        {onDelete && allowCalculate && (
          <>
            <Tooltip text="Usuń ewidencję" onClick={onDelete}>
              <div className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faTrash}
                  className="tw-h-4 tw-w-4 tw-text-red-500"
                />
                <span className="tw-text-sm tw-font-medium tw-text-red-500">
                  Usuń
                </span>
              </div>
            </Tooltip>
            <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]" />
          </>
        )}

        <Tooltip
          text={t('timesheet.print-reports')}
          onClick={onPrint}
          blockedText={busyTooltip}
        >
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faPrint}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>
        {printShortcuts}

        <div className="tw-h-[18px] tw-w-px tw-bg-[#e5e6e6]" />

        <Tooltip text={t('timesheet.refresh')} onClick={onRefresh}>
          <div className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
            <FaIcon
              icon={faArrowsRotate}
              className="tw-h-4 tw-w-4 tw-text-[#2448bb]"
            />
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

export default TimeSheetActionBar
